import type { WorkAssignment } from 'components/IAP/hooks/workAssignmentHooks';

export function assignmentKey(work: WorkAssignment): string {
  return work.work_assignment_id || work.id;
}

export function cloneAssignments(
  assignments: WorkAssignment[]
): WorkAssignment[] {
  return assignments.map((work) => ({
    ...work,
    resource_list: (work.resource_list ?? []).map((item) => ({ ...item })),
    parent_tactics: work.parent_tactics ? [...work.parent_tactics] : [],
  }));
}

function normalizeArrival(value: Date | string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

/**
 * Stable snapshot of one work assignment's editable fields.
 *
 * Each row is tracked and saved on its own, so comparison has to be per row rather than across the whole grid.
 */
export function serializeAssignment(work: WorkAssignment): string {
  return JSON.stringify({
    key: assignmentKey(work),
    archived: !!work.archived,
    work_assignment: work.work_assignment ?? '',
    work_group_id: work.work_group_id ?? '',
    work_group_name: work.work_group_name ?? '',
    overhead_positions: work.overhead_positions ?? '',
    special_instructions: work.special_instructions ?? '',
    reporting_location: work.reporting_location ?? '',
    requested_arrival_time: normalizeArrival(work.requested_arrival_time),
    status: work.status ?? '',
    resource_list: (work.resource_list ?? [])
      .map((item) => ({
        resourceName: item.resourceName,
        required: Number(item.required) || 0,
        have: Number(item.have) || 0,
        need: Number(item.need) || 0,
      }))
      .sort((a, b) => a.resourceName.localeCompare(b.resourceName)),
  });
}

/** Stable snapshot of editable work-assignment fields for dirty checks. */
export function serializeAssignmentsDraft(
  assignments: WorkAssignment[],
  extraResourceColumns: string[]
): string {
  const rows = assignments
    .map((work) => ({ key: assignmentKey(work), row: serializeAssignment(work) }))
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((entry) => entry.row);

  return JSON.stringify({
    rows,
    extraResourceColumns: [...extraResourceColumns].sort(),
  });
}

export function assignmentChanged(
  draft: WorkAssignment,
  server: WorkAssignment | undefined
): boolean {
  if (!server) return true;
  return serializeAssignment(draft) !== serializeAssignment(server);
}

/** The last-known-clean serialization of each row, keyed by assignment. */
export function buildBaselineByKey(
  assignments: WorkAssignment[]
): Record<string, string> {
  const baseline: Record<string, string> = {};
  for (const work of assignments) {
    baseline[assignmentKey(work)] = serializeAssignment(work);
  }
  return baseline;
}

/** Keys of the rows whose draft differs from the baseline captured for that row. */
export function dirtyAssignmentKeys(
  drafts: WorkAssignment[],
  baselineByKey: Record<string, string>
): Set<string> {
  const keys = new Set<string>();
  for (const draft of drafts) {
    const key = assignmentKey(draft);
    if (baselineByKey[key] !== serializeAssignment(draft)) {
      keys.add(key);
    }
  }
  return keys;
}
